import React, { useEffect, useState } from 'react';
import { Modal, Button, Dropdown, Form } from 'react-bootstrap';
import './embedContentModal.scss';
import EmbedTable from './embedTable';
import { Editor } from '@tiptap/core';
import { ReactComponent as GlobalIcon } from '../../../assets/icons/globalIcon.svg';
import { ReactComponent as PrivateIcon } from '../../../assets/icons/sharedIcon.svg';
import { ReactComponent as SharedIcon } from '../../../assets/icons/privateIcon.svg';
import { ReactComponent as DropDownIcon } from '../../../assets/icons/dropdown.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/searchIcon.svg';
import { ReactComponent as HeaderIcon } from '../../../assets/icons/embedHeaderIcon.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refreshIcon.svg';
import { InteractiveTooltip } from '../../utils/interactivetooltip';
import { InteractiveItem, items } from './embedUtil';
import { useDispatch, useSelector } from 'react-redux';
import { ParentDataInterface } from '../../../redux/parentDataSlice';
import { addType } from '../../../redux/embedInterctive';

interface ImageModalProps {
  show: boolean;
  setShowEmbedModal: (show: boolean) => void;
  editor: Editor;
}
interface IconData {
  key: string;
  className: string;
  IconComponent: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tooltipInfo: string;
}

const icons: IconData[] = [
  {
    key: 'globalIcon',
    className: 'globalIcon',
    IconComponent: GlobalIcon,
    tooltipInfo: 'Global Interactive',
  },
  {
    key: 'privateIcon',
    className: 'privateIcon',
    IconComponent: PrivateIcon,
    tooltipInfo: 'Private Interactive',
  },
  {
    key: 'sharedIcon',
    className: 'sharedIcon',
    IconComponent: SharedIcon,
    tooltipInfo: 'Shared Interactive',
  },
];
const EmbedImageModal: React.FC<ImageModalProps> = ({ show, setShowEmbedModal, editor }) => {
  const [selectedSource, setSelectedSource] = useState('url');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedInteractive, setSelectedInteractive] = useState('Private Interactive');
  const [selectedRowData, setSelectedRowData] = useState<InteractiveItem[]>([]);
  const [selectedTabUrl, setSelectedTabUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [previewData, setPreviewData] = useState<null | { url: string; title: string; id: string }>(
    null,
  );
  const [externalUrl, setExternalUrl] = useState('');
  const [externalLinkTitle, setExternalLinkTitle] = useState('');
  const [linkWidth, setLinkWidth] = useState<string | number>(100);
  const [linkHeight, setLinkHeight] = useState<string | number>(400);
  const [isSearchText, setIsSearchText] = useState(false);
  const [activeTab, setActiveTab] = useState('privateIcon');
  const [enablePreviousBtn, setEnablePreviousBtn] = useState(true);
  const [showNextBtn, setShowNextBtn] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(true);
  const [selectedTypeLabel, setSelectedTypeLabel] = useState('All Interactives');
  const [isValid, setIsValid] = useState(false);
  const [errorInExternalLink, setErrorInExternalLink] = useState(true);
  const [refresh, setRefresh] = useState<string | null>(null);
  const dataFromParent = useSelector((state: ParentDataInterface) => state.parentData.data);
  const dispatch = useDispatch();
  useEffect(() => {
    if (selectedRowData.length > 0) {
      const generateUrl = `${process.env.REACT_APP_CLIENT_URL}/${selectedRowData[0].library_type}/${selectedRowData[0].id}?ltiPlatformId=${dataFromParent.ltiPlatformId}`;
      setSelectedTabUrl(generateUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowData]);
  const handleSelect = (value: string) => {
    setSelectedSource(value);
    setSelectedType('all');
    setIsSearchText(false);
    setSearchTerm('');
    setSelectedTypeLabel('All Interactives');
    setSelectedInteractive('Private Interactive');
    setActiveTab('privateIcon');
  };
  useEffect(() => {
    if (isFullWidth) {
      setLinkWidth(100);
    }
  }, [isFullWidth]);
  const insertMediaToSection = () => {
    const iframeWidth = isFullWidth ? 100 : linkWidth;
    let content: string;
    if (selectedSource !== 'external') {
      content = `<iframe class="loree-iframe-content-interactive-element" title=${externalLinkTitle} src=${
        selectedTabUrl as string
      } id=${
        selectedRowData[0].id
      } frameborder="0" contenteditable="false" scrolling="no" width="${iframeWidth}%" height="100%"></iframe>
`;
    } else if (externalUrl.includes('iframe')) {
      const newWidth = `${iframeWidth}%`;
      const newHeight = `${linkHeight}px`;
      content = externalUrl.replace(/width="[^"]*"/, `width="${newWidth}"`);
      content = content.replace(/height="[^"]*"/, `height="${newHeight}"`);
      content = content.replace(/title="[^"]*"/, `title="${externalLinkTitle}"`);
    } else {
      content = externalUrl;
    }
    editor.chain().focus().insertContent(content).run();
    setShowEmbedModal(false);
  };
  const handlePreview = () => {
    if (selectedRowData.length > 0) {
      setPreviewData({
        url: selectedTabUrl as string,
        title: selectedRowData[0]?.title,
        id: selectedRowData[0]?.id?.toString() ?? '',
      });
      setExternalLinkTitle(selectedRowData[0]?.title);
    } else {
      setPreviewData({
        url: externalUrl,
        title: '',
        id: '',
      });
    }
    setShowPreviewModal(true);
  };
  const handleExternalLinkUrl = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setErrorInExternalLink(true);
    const value = e.target.value;
    setExternalUrl(value);
    const trimmedValue = value.trim();
    const iframeRegex =
      /^<iframe\b(?=[^>]{0,2000}\ssrc=(?:"[^"]+"|'[^']+'))[^>]{0,2000}>\s*<\/iframe>$/i;
    setIsValid(iframeRegex.test(trimmedValue.trim()));
    setErrorInExternalLink(
      (trimmedValue.length > 0 && iframeRegex.test(trimmedValue.trim())) || trimmedValue === '',
    );
  };
  const headerComponent = () => {
    return (
      <div className='embed-source-options px-3 py-2'>
        <button
          className={`embed-option ${selectedSource === 'url' ? 'active' : ''}`}
          onClick={() => {
            handleSelect('url');
            setSelectedRowData([]);
            setExternalUrl('');
            setErrorInExternalLink(true);
          }}
        >
          Interactives
        </button>
        <button
          className={`embed-option ${selectedSource === 'external' ? 'active' : ''}`}
          onClick={() => {
            handleSelect('external');
            setSelectedRowData([]);
            setExternalUrl('');
            setErrorInExternalLink(true);
          }}
        >
          External URL
        </button>
      </div>
    );
  };

  const widthAndHeightComponent = () => {
    return (
      <div className='manageHeight'>
        <div className='d-flex gap-2'>
          <div className='d-flex flex-column gap-2 w-100'>
            <label className='embed-title-input-label' htmlFor='embed-title-input'>
              Width(%)
            </label>
            <input
              id='embed-title-input'
              type='number'
              min={0}
              max={100}
              defaultValue={100}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  return setLinkWidth('');
                }
                setLinkWidth(Number(value));
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData('text');
                if (!/^\d+$/.test(pasted) || Number(pasted) > 100) {
                  e.preventDefault();
                }
              }}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              value={isFullWidth ? 100 : linkWidth}
              disabled={isFullWidth}
              data-testid='width'
            />
          </div>
          {selectedSource === 'external' && (
            <div className='d-flex flex-column gap-2'>
              <label className='embed-title-input-label' htmlFor='embed-title-input'>
                Height(px)
              </label>
              <input
                defaultValue={400}
                id='embed-title-input'
                type='number'
                min={0}
                onChange={(e) => {
                  const heightvalue = e.target.value;
                  if (heightvalue === '') return setLinkHeight('');
                  setLinkHeight(Number(heightvalue));
                }}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text');
                  if (!/^\d+$/.test(pasted) || Number(pasted) > 100) {
                    e.preventDefault();
                  }
                }}
                data-testid='height'
              />
            </div>
          )}
        </div>
        <div>
          <label className='embed-title-input-label d-flex gap-2'>
            <input
              id='fullwidthCheckbox'
              type='checkbox'
              defaultChecked
              onChange={(e) => setIsFullWidth(e.target.checked)}
            />{' '}
            Full Width
          </label>
        </div>
      </div>
    );
  };
  const renderModalBody = () => {
    if (showPreviewModal) {
      return (
        <>
          <div
            className='p-3'
            style={{
              height: '400px',
              margin: '16px',
              border: '1px solid #e9e9e9',
              borderRadius: '12px',
              overflowY: 'scroll',
            }}
          >
            {externalUrl === '' ? (
              <iframe
                className='loree-iframe-content-interactive-element'
                title={previewData?.title}
                src={previewData?.url}
                id={previewData?.id}
                style={{ width: '100%', height: '100%', maxWidth: '100%', overflowY: 'scroll' }}
              />
            ) : (
              <div
                className='externalLinkPreview h-100'
                dangerouslySetInnerHTML={{ __html: externalUrl }}
              />
            )}
          </div>
          <div className='widthHeightWrapper p-3'>
            <div className='d-flex flex-column gap-2' style={{ width: '80%' }}>
              <label className='embed-title-input-label' htmlFor='embed-title-input'>
                Title <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                id='embed-title-input'
                type='text'
                onChange={(e) => setExternalLinkTitle(e.target.value)}
                value={externalLinkTitle}
              />
            </div>
            {widthAndHeightComponent()}
          </div>
        </>
      );
    }
    const handleClick = (icon: IconData) => {
      setSelectedInteractive(icon.tooltipInfo);
      setActiveTab(icon.key);
      setSelectedRowData([]);
      setSelectedType('all');
      setIsSearchText(false);
      setSearchTerm('');
      setSelectedTypeLabel('All Interactives');
    };
    const renderIconButton = (icon: IconData) => (
      <InteractiveTooltip info={icon.tooltipInfo} direction='right' key={icon.key}>
        <button
          className={`${icon.className} embed p-3 ${activeTab === icon.key ? 'active' : ''}`}
          tabIndex={0}
          onClick={() => handleClick(icon)}
        >
          <icon.IconComponent width={20} height={20} />
        </button>
      </InteractiveTooltip>
    );
    const renderDropdownItem = (item: any) => (
      <Dropdown.Item
        key={item.key}
        className='custom-dropdown-item'
        onClick={() => handleDropdownClick(item)}
        active={selectedType === item.key}
      >
        {item.label}
      </Dropdown.Item>
    );
    const handleDropdownClick = (item: any) => {
      setSelectedType(item.key);
      setSelectedTypeLabel(item.label);
    };

    const dropDownComponent = () => {
      return (
        <div className='d-flex justify-content-between align-items-center'>
          <Dropdown>
            <Dropdown.Toggle
              variant='light'
              id='interactive-dropdown'
              className='border custom-dropdown-toggle'
              style={{ minWidth: '200px', textAlign: 'left' }}
              data-testid='interactive-dropdown'
            >
              {selectedTypeLabel}
              <DropDownIcon />
            </Dropdown.Toggle>

            <Dropdown.Menu className='custom-dropdown-menu'>
              {items.map(renderDropdownItem)}
            </Dropdown.Menu>
          </Dropdown>
          <button
            className='d-flex justify-content-center align-items-center'
            onClick={() => {
              setRefresh(selectedInteractive);
              setSelectedType('all');
              setIsSearchText(false);
              setSearchTerm('');
              setSelectedTypeLabel('All Interactives');
              dispatch(addType(selectedInteractive));
            }}
            style={{
              border: 'none',
              background: 'transparent',
              height: '34px',
              gap: '4px',
            }}
          >
            <RefreshIcon className={refresh ? 'spin' : ''} />
            <div className='refreshIconLabel'>Refresh</div>
          </button>
        </div>
      );
    };
    const filterComponent = () => {
      return (
        <div className='d-flex justify-content-between mb-2 leftWrapper' style={{ height: '10%' }}>
          <div className='titleOfInteractive'>List of interactive</div>
          <div className='d-flex align-content-center filterWrapper justify-content-end gap-2 w-100'>
            <div className='d-inline-flex align-content-center position-relative'>
              <input
                name='search'
                className={`search interactives-searchbar pe-4 ${!isSearchText ? 'd-none' : ''}`}
                type='text'
                autoComplete='off'
                placeholder='Search'
                data-testid='debounce-search'
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.startsWith(' ')) {
                    return;
                  }
                  setSearchTerm(value);
                  setSelectedRowData([]);
                }}
                value={searchTerm}
                style={{
                  fontFamily: 'Lexend',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 350,
                  border: '1px solid #e9e9e9',
                  borderRadius: '8px',
                  padding: '8px',
                  height: '38px',
                }}
              />
              <div
                className='search-icon-div d-flex justify-content-center align-items-center'
                style={{ transform: 'translate(-30px,-4px)' }}
              >
                <SearchIcon
                  data-testid='search-icon'
                  width={!isSearchText ? 24 : 15}
                  height={!isSearchText ? 20 : 15}
                  style={isSearchText ? { top: 8, right: 0 } : {}}
                  className={!isSearchText ? 'm-2 search-normal-icon' : 'mx-2 iconActive'}
                  onClick={() => {
                    setIsSearchText(!isSearchText);
                  }}
                />
              </div>
            </div>
            {dropDownComponent()}
          </div>
        </div>
      );
    };
    const sideBarComponent = () => {
      return <div className='icon-container-nav py-3'>{icons.map(renderIconButton)}</div>;
    };
    return (
      <>
        {headerComponent()}
        {selectedSource === 'external' ? (
          <div className='d-flex flex-row gap-3 p-3 w-100' style={{ height: '450px' }}>
            <Form.Group className='w-100 mb-3'>
              <Form.Label className='embedUrlHeader'>
                Insert Embed URL <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                as='textarea'
                className={`linkInputBox ${!errorInExternalLink ? 'error' : ''}`}
                placeholder='Insert External URL'
                onChange={handleExternalLinkUrl}
              />
              <Form.Text className='text-muted embedUrlNote'>
                Note: Embed URL needs to be in format: {' <iframe src="">....</iframe> '}
              </Form.Text>
            </Form.Group>
          </div>
        ) : (
          <div className='interActiveWrapper' style={{ borderTop: '1px solid #e9e9e9' }}>
            {sideBarComponent()}
            <div className='w-100 p-3' style={{ borderLeft: '1px solid #e9e9e9' }}>
              {filterComponent()}
              <div style={{ height: '400px', overflowY: 'scroll' }}>
                <EmbedTable
                  selectedInteractive={selectedInteractive}
                  selectedType={selectedType}
                  setSelectedRowData={setSelectedRowData}
                  searchTerm={searchTerm}
                  refresh={refresh}
                  setRefresh={setRefresh}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  };
  return (
    <Modal
      show={show}
      onHide={() => {
        setShowEmbedModal(false);
      }}
      centered
      className='p-0 embedInteractive'
      backdrop='static'
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className='modalTitle'>
          <HeaderIcon style={{ marginRight: '8px' }} />
          Embed
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='p-0'>{renderModalBody()}</Modal.Body>

      <Modal.Footer className='d-flex justify-content-center'>
        <Button
          className='previousBtn'
          disabled={enablePreviousBtn}
          onClick={() => {
            setEnablePreviousBtn(true);
            setPreviewData(null);
            setShowPreviewModal(false);
            setShowNextBtn(true);
            setSelectedRowData([]);
            setIsValid(false);
            setExternalUrl('');
            setLinkWidth(100);
            setExternalLinkTitle('');
            setLinkHeight(400);
          }}
        >
          Previous
        </Button>
        <Button
          className='cancelBtn'
          onClick={() => {
            setShowEmbedModal(false);
          }}
        >
          Cancel
        </Button>
        {!showPreviewModal && (
          <Button
            className='nextBtn'
            onClick={() => {
              setShowNextBtn(false);
              handlePreview();
              setEnablePreviousBtn(false);
            }}
            disabled={selectedRowData.length === 0 && !isValid}
          >
            Next
          </Button>
        )}
        {!showNextBtn && (
          <Button
            className='embedBtn'
            onClick={() => {
              insertMediaToSection();
            }}
            disabled={
              externalLinkTitle.trim().length === 0 ||
              linkWidth == '' ||
              linkHeight === '' ||
              linkHeight === 0
            }
          >
            Embed
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EmbedImageModal;
